import { lazy, type ComponentType, type LazyExoticComponent } from 'react'

import { captureException, withScope } from './SentryHelper'
import { sendErrorToSlack } from './SlackHelper'

const VITE_PRELOAD_RECOVERY_KEY = 'intraction_vite_preload_recovery'

type VitePreloadErrorEvent = Event & { payload?: unknown }

const preloadErrorSignature = (payload: unknown): string => {
  if (payload instanceof Error) return `${payload.name}:${payload.message}`
  return String(payload ?? 'unknown-preload-error')
}

/**
 * Vite emits this event when a deployment removed a hashed chunk still referenced by an open tab.
 * Reload once so the tab gets the current app shell; remembering the exact failure prevents a loop if
 * the deployment itself is broken.
 */
export function recoverFromVitePreloadError(event: Event, reload = () => window.location.reload()): boolean {
  const signature = preloadErrorSignature((event as VitePreloadErrorEvent).payload)
  try {
    if (sessionStorage.getItem(VITE_PRELOAD_RECOVERY_KEY) === signature) return false
    sessionStorage.setItem(VITE_PRELOAD_RECOVERY_KEY, signature)
  } catch {
    // Without a persistent guard, reloading could loop forever in a locked-down browser.
    return false
  }

  event.preventDefault()
  reload()
  return true
}

/** Install before any application-owned dynamic import can run. */
export function installVitePreloadErrorRecovery(): void {
  window.addEventListener('vite:preloadError', recoverFromVitePreloadError)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- any props, as React's own lazy() takes
type AnyComponent = ComponentType<any>

/** Never settles, so a lazy component keeps its loader on screen until the reload replaces the page. */
const untilReloaded = <Value>(): Promise<Value> => new Promise<Value>(() => undefined)

/** A component loaded on first render, by its export name; it waits out the reload a stale chunk starts. */
export function lazyComponent<Module extends Record<Name, AnyComponent>, Name extends keyof Module>(
  load: () => Promise<Module>,
  name: Name
): LazyExoticComponent<Module[Name]> {
  return lazy(async () => {
    const module: Module | undefined = await load()
    if (!module) return untilReloaded<{ default: Module[Name] }>()

    return { default: module[name] }
  })
}

/**
 * Handler for unhandled promise rejections
 * Logs errors and sends critical ones to Slack and Sentry
 */
export default function onUnhandledPromiseRejection(error: unknown) {
  // Convert to Error object if not already
  let errorObj: Error
  if (error instanceof Error) {
    errorObj = error
  } else if (typeof error === 'string') {
    errorObj = new Error(error)
  } else {
    errorObj = new Error(JSON.stringify(error))
  }

  // Get user info from session storage
  let user: { email?: string; id?: string } | undefined
  try {
    const userStr = sessionStorage.getItem('user')
    if (userStr) {
      const userData = JSON.parse(userStr)
      user = { email: userData.email, id: userData.id }
    }
  } catch {
    // Ignore parsing errors
  }

  // Send to Slack
  sendErrorToSlack(errorObj, user, 'Unhandled Promise Rejection').catch(() => {
    // Ignore Slack errors
  })

  // Send to Sentry (dual reporting)
  withScope((scope) => {
    scope.setContext('source', { type: 'unhandledPromiseRejection' })
    if (user) scope.setUser({ id: user.id, email: user.email })
    captureException(errorObj)
  })
}
